/**
 * What each kind of institution starts with.
 *
 * A university has no use for Cambridge Checkpoint, a training centre none for Tahfiz, and plenty
 * of K-12 schools are secular — every module a school will never open is one more thing its staff
 * wade past. Tahfiz and Musabaqa are therefore off everywhere except an Islamic centre, and are
 * added on request rather than handed out by default.
 *
 * These are only starting points. `enabledFeatures` on the school stays the one place that decides
 * what is on, so a super admin can tick anything back on for a school that asks — a college running
 * Cambridge A-Levels, or a secondary school with a Qur'an programme, is one click away.
 *
 * A school with no `enabledFeatures` at all still means "everything on". These presets change what
 * a NEW school is approved with, and what the Apply preset button offers an existing one. Nobody
 * loses a module they are already using.
 */
import type { InstitutionType, SchoolFeature } from '../types';

/** every module there is */
const EVERYTHING: SchoolFeature[] = [
    'presentations', 'live_classes', 'cbt', 'cambridge', 'lms', 'ai_tutor',
    'tahfiz', 'musabaqa', 'finance', 'iqa', 'scholarships', 'games',
    'analytics', 'aicpd', 'admissions',
];

const without = (...drop: SchoolFeature[]) => EVERYTHING.filter(f => !drop.includes(f));

/** asked for, not assumed: a secular school should never have to explain why it has these */
const ISLAMIC: SchoolFeature[] = ['tahfiz', 'musabaqa'];

export const INSTITUTION_PRESETS: Record<InstitutionType, SchoolFeature[]> = {
    // Runs the whole curriculum, so it keeps the whole platform bar the Islamic modules.
    'K-12 School': without(...ISLAMIC),

    // Qur'an memorisation and competition are the point here — the one category that starts with them.
    'Islamic Center': EVERYTHING,

    // Adults on short courses: no Checkpoint, no primary-school games, and admissions is built
    // around K-12 enrolment rather than course sign-ups.
    'Training Center': without('cambridge', 'games', 'admissions', ...ISLAMIC),
    'Vocational Institution': without('cambridge', 'games', 'admissions', ...ISLAMIC),

    // Higher education: Checkpoint and the primary games go; admissions stays, since a university
    // does admit students even if the form still speaks K-12.
    'College/University': without('cambridge', 'games', ...ISLAMIC),

    // One person teaching: no admissions office and no quality-assurance department to run.
    'Individual Online Tutor': without('cambridge', 'admissions', 'iqa', ...ISLAMIC),
};

export const DEFAULT_INSTITUTION: InstitutionType = 'K-12 School';

/** The institution types offered wherever a school is applied for, created or edited. */
export const INSTITUTION_TYPES: { value: InstitutionType; label: string }[] = [
    { value: 'K-12 School', label: 'K-12 School' },
    { value: 'Islamic Center', label: 'Madrasa / Islamic Center' },
    { value: 'Training Center', label: 'Training Center' },
    { value: 'Vocational Institution', label: 'Vocational Institution' },
    { value: 'College/University', label: 'College/University' },
    { value: 'Individual Online Tutor', label: 'Individual Online Tutor' },
];

/** Older records hold free text — "Madrasa / Islamic School", "Vocational", a blank. Read it kindly. */
export const asInstitutionType = (raw?: string): InstitutionType => {
    const v = (raw || '').trim();
    if (INSTITUTION_PRESETS[v as InstitutionType]) return v as InstitutionType;
    if (/madrasa|islamic|quran|qur'an|tahfiz/i.test(v)) return 'Islamic Center';
    if (/vocational/i.test(v)) return 'Vocational Institution';
    if (/college|university|polytechnic|higher/i.test(v)) return 'College/University';
    if (/training/i.test(v)) return 'Training Center';
    if (/tutor|individual|freelance/i.test(v)) return 'Individual Online Tutor';
    return DEFAULT_INSTITUTION;
};

export const presetFor = (raw?: string): SchoolFeature[] => INSTITUTION_PRESETS[asInstitutionType(raw)];
